import { useState, useCallback } from 'react';

interface UsePaginationReturn {
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setTotalPages: (pages: number) => void;
  setTotalCount: (count: number) => void;
  handlePageChange: (newPage: number) => void;
  reset: () => void;
}

/**
 * Reusable pagination hook
 * @param initialPage Initial page number (default: 1)
 * @param initialPageSize Initial page size (default: 10)
 * @returns Pagination state and handlers
 */
export const usePagination = (
  initialPage: number = 1,
  initialPageSize: number = 10
): UsePaginationReturn => {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page, totalPages]);

  const reset = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
    setTotalPages(1);
    setTotalCount(0);
  }, [initialPage, initialPageSize]);

  return {
    page,
    pageSize,
    totalPages,
    totalCount,
    setPage,
    setPageSize,
    setTotalPages,
    setTotalCount,
    handlePageChange,
    reset
  };
};
